import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import WaveDivider from '../components/WaveDivider';

export default function HomePage() {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    baad: '',
    beskrivelse: '',
    website_url: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [formStatus, setFormStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [formErrorMessage, setFormErrorMessage] = useState('');

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.navn.trim()) errs.navn = 'Venligst indtast dit navn';
    if (!formData.telefon.trim()) errs.telefon = 'Venligst indtast dit telefonnummer';
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast din emailadresse';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Indtast venligst en gyldig emailadresse';
    }
    if (!formData.beskrivelse.trim()) errs.beskrivelse = 'Venligst beskriv din opgave';
    return errs;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    setFormStatus('submitting');
    setFormErrorMessage('');

    try {
      const payload = {
        navn: formData.navn,
        telefon: formData.telefon,
        email: formData.email,
        baad_model: formData.baad || 'Ikke angivet',
        beskrivelse: formData.beskrivelse,
        website_url: formData.website_url // Honeypot
      };

      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setFormStatus('success');
        setFormData({
          navn: '',
          telefon: '',
          email: '',
          baad: '',
          beskrivelse: '',
          website_url: ''
        });
      } else {
        const errorData = await res.json().catch(() => ({}));
        setFormStatus('error');
        setFormErrorMessage(
          errorData.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen eller ring direkte.'
        );
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setFormStatus('error');
      setFormErrorMessage(
        'Kunne ikke sende formularen. Tjek din internetforbindelse eller ring direkte på +45 50 51 00 62.'
      );
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <SEO
        title="Bådtransport | Flytning af båd i Mårslet, Aarhus, Horsens & omegn"
        description="Bådtransport i Mårslet, Aarhus, Horsens og omegn. God service til en god pris. Kontakt mig for flytning af båd i Mårslet, Aarhus og Horsens."
        ogTitle="Bådtransport | Flytning af båd i Mårslet, Aarhus, Horsens & omegn"
        ogDescription="Bådtransport i Mårslet, Aarhus, Horsens og omegn. God service til en god pris. Kontakt mig for flytning af båd i Mårslet, Aarhus og Horsens."
        ogImage="https://assets.auxodev.dk/assets/a8b5f246-9eaa-4cb1-adf9-4b882abefbbd/gettyimages-170088390.jpg"
      />

      {/* HERO SECTION WITH VIDEO */}
      <section className="relative min-h-[96vh] flex items-center justify-center overflow-hidden bg-maritime-950 text-white pt-36 sm:pt-44 lg:pt-52 pb-24 lg:pb-36">
        {/* Background Video */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="https://assets.auxodev.dk/assets/a8b5f246-9eaa-4cb1-adf9-4b882abefbbd/hero-sejlads-dansk-farvand.jpg"
            className="w-full h-full object-cover opacity-75 sm:opacity-80 scale-105 transition-all duration-1000"
          >
            <source
              src="https://assets.auxodev.dk/assets/a8b5f246-9eaa-4cb1-adf9-4b882abefbbd/hero-video-1789368470633.mp4"
              type="video/mp4"
            />
          </video>
          {/* Transparent maritime overlays for cinematic water clarity */}
          <div className="absolute inset-0 bg-gradient-to-t from-maritime-950/85 via-maritime-950/30 to-maritime-950/40" />
          <div className="absolute inset-0 bg-black/20" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center mt-2 sm:mt-4 [text-shadow:_0_2px_12px_rgba(0,0,0,0.9),_0_1px_3px_rgba(0,0,0,0.95)]">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-950/85 border border-cyan-400/50 text-cyan-300 text-xs sm:text-sm font-semibold tracking-wider uppercase mb-6 backdrop-blur-md shadow-lg [text-shadow:_0_1px_6px_rgba(0,0,0,0.9)]">
            <i className="fa-solid fa-anchor text-cyan-400 drop-shadow" aria-hidden="true" />
            <span>Velkommen til JT transportsejlads</span>
          </div>

          {/* Primary Position Headline: DIN BÅDTRANSPORTØR PÅ VANDET */}
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-white leading-tight mb-6 [text-shadow:_0_3px_16px_rgba(0,0,0,0.95),_0_1px_4px_rgba(0,0,0,0.9)]">
            Din bådtransportør <span className="text-cyan-400 block sm:inline [text-shadow:_0_3px_16px_rgba(0,0,0,0.95),_0_1px_4px_rgba(0,0,0,0.9)]">på vandet</span>
          </h1>

          {/* Scraped Headline & Intro */}
          <p className="text-xl sm:text-2xl font-semibold text-slate-100 mb-4 max-w-3xl mx-auto [text-shadow:_0_2px_10px_rgba(0,0,0,0.95),_0_1px_3px_rgba(0,0,0,0.9)]">
            Bådtransport – sikkert, professionelt og til tiden
          </p>

          <p className="text-base sm:text-lg text-slate-200 max-w-2xl mx-auto mb-8 sm:mb-10 font-normal leading-relaxed [text-shadow:_0_2px_10px_rgba(0,0,0,0.95),_0_1px_3px_rgba(0,0,0,0.9)]">
            Lad mig hjælpe dig med dit næste projekt. Jeg tilbyder professionel hjælp til en lang række opgaver. Kontakt mig i dag for at høre mere om, hvad jeg kan gøre for dig.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 [text-shadow:none]">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold text-base shadow-xl shadow-black/40 hover:shadow-cyan-500/30 transition-all transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-cyan-400"
            >
              <span>Kontakt mig i dag</span>
              <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
            </Link>

            <a
              href="tel:+4550510062"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-xl bg-maritime-900/80 hover:bg-maritime-800/90 text-white font-semibold text-base border border-white/30 hover:border-white/50 transition-all backdrop-blur-md shadow-xl shadow-black/30 whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-cyan-400"
            >
              <i className="fa-solid fa-phone text-cyan-400" aria-hidden="true" />
              <span className="whitespace-nowrap">Ring på +45 50 51 00 62</span>
            </a>
          </div>

          {/* Key Maritime Highlights */}
          <div className="mt-14 pt-8 border-t border-white/20 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 text-left">
            <div className="p-3 bg-maritime-950/60 rounded-xl border border-white/15 backdrop-blur-md shadow-md">
              <span className="block text-cyan-400 font-bold text-xs uppercase tracking-wider [text-shadow:_0_1px_4px_rgba(0,0,0,0.9)]">Erfaring</span>
              <span className="text-white text-sm font-semibold [text-shadow:_0_1px_4px_rgba(0,0,0,0.9)]">Skipper siden 2012</span>
            </div>
            <div className="p-3 bg-maritime-950/60 rounded-xl border border-white/15 backdrop-blur-md shadow-md">
              <span className="block text-cyan-400 font-bold text-xs uppercase tracking-wider [text-shadow:_0_1px_4px_rgba(0,0,0,0.9)]">Uddannelse</span>
              <span className="text-white text-sm font-semibold [text-shadow:_0_1px_4px_rgba(0,0,0,0.9)]">Duelighedsbevis 2006</span>
            </div>
            <div className="p-3 bg-maritime-950/60 rounded-xl border border-white/15 backdrop-blur-md shadow-md">
              <span className="block text-cyan-400 font-bold text-xs uppercase tracking-wider [text-shadow:_0_1px_4px_rgba(0,0,0,0.9)]">Område</span>
              <span className="text-white text-sm font-semibold [text-shadow:_0_1px_4px_rgba(0,0,0,0.9)]">Hele Østjylland</span>
            </div>
            <div className="p-3 bg-maritime-950/60 rounded-xl border border-white/15 backdrop-blur-md shadow-md">
              <span className="block text-cyan-400 font-bold text-xs uppercase tracking-wider [text-shadow:_0_1px_4px_rgba(0,0,0,0.9)]">Transportform</span>
              <span className="text-white text-sm font-semibold [text-shadow:_0_1px_4px_rgba(0,0,0,0.9)]">Sejlads på vandet</span>
            </div>
          </div>
        </div>

        {/* Wave divider to next section */}
        <div className="absolute bottom-0 inset-x-0">
          <WaveDivider fill="#f8fafc" />
        </div>
      </section>

      {/* SECTION 1: NÅR DIN BÅD SKAL TRANSPORTERES SIKKERT OG TIL TIDEN */}
      <section className="py-16 sm:py-20 lg:py-24 bg-slate-50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Text side */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 text-cyan-700 font-semibold text-xs sm:text-sm tracking-widest uppercase">
                <i className="fa-solid fa-ship" aria-hidden="true" />
                <span>JT transportsejlads</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight leading-snug">
                Når din båd skal transporteres sikkert og til tiden
              </h2>

              <div className="space-y-4 text-slate-700 text-base sm:text-lg leading-relaxed">
                <p>
                  Skal du have udført bådtransport i Mårslet, Aarhus, Horsens eller omegn? Så kan jeg hjælpe dig med en sikker og effektiv flytning af båd – uanset om det drejer sig om en mindre sejlbåd eller et større fartøj. Hos JT Transportsejlads har jeg mange års erfaring med transport af båd på land og over vand, og jeg sørger altid for, at din båd kommer frem i samme stand, som den blev afhentet.
                </p>
                <p>
                  Jeg tilbyder bådtransport i hele Østjylland, og jeg planlægger altid turen, så den foregår trygt, professionelt og til tiden.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to="/om-mig"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-maritime-900 hover:bg-maritime-800 text-white font-semibold text-sm transition-colors shadow-md"
                >
                  <span>Læs mere om mig</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>

                <Link
                  to="/baadtransport"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white hover:bg-slate-100 text-slate-900 font-semibold text-sm border border-slate-300 transition-colors"
                >
                  <span>Bådtransport detaljer</span>
                  <i className="fa-solid fa-chevron-right text-xs text-cyan-600" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Visual side - High quality sailing image on water */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white aspect-[4/3] bg-slate-200">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/a8b5f246-9eaa-4cb1-adf9-4b882abefbbd/gettyimages-170088390.jpg"
                  alt="Hvid motorbåd der sejler i høj fart på blåt hav"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="800"
                  height="600"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-maritime-950/80 via-transparent to-transparent" />
              </div>

              {/* Floating Stat card */}
              <div className="absolute -bottom-6 -left-6 sm:bottom-4 sm:-left-8 bg-white rounded-xl shadow-xl p-4 border border-slate-200 hidden sm:flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center text-xl">
                  <i className="fa-solid fa-compass" aria-hidden="true" />
                </div>
                <div>
                  <span className="block text-xs text-slate-500 font-medium">Uddannet skipper</span>
                  <span className="block text-sm font-bold text-slate-900">Sikker sejlads til tiden</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: HVAD INDEBÆRER EN BÅDTRANSPORT & FLEKSIBEL BÅDTRANSPORT */}
      <section className="py-16 sm:py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Box 1: Hvad indebærer en bådtransport? */}
            <div className="bg-slate-50 rounded-2xl p-8 sm:p-10 border border-slate-200/80 shadow-sm flex flex-col justify-between">
              <div className="space-y-5">
                <div className="w-12 h-12 rounded-xl bg-cyan-600 text-white flex items-center justify-center text-xl shadow-md">
                  <i className="fa-solid fa-list-check" aria-hidden="true" />
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                  Hvad indebærer en bådtransport?
                </h2>

                <p className="text-slate-700 leading-relaxed">
                  Når du skal have flyttet din båd, handler det om meget mere end blot at spænde den fast og køre afsted. En korrekt bådtransport kræver udstyr, erfaring og præcision – og det er netop her, jeg kan hjælpe dig.
                </p>

                <p className="font-semibold text-slate-900 text-sm tracking-wide uppercase">
                  Jeg håndterer hele processen fra start til slut:
                </p>

                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </span>
                    <span>Klargøring og sikker fastspænding af båden</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </span>
                    <span>Kørsel med specialtrailer tilpasset bådens størrelse og vægt</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </span>
                    <span>Levering og aflæsning på ønsket destination</span>
                  </li>
                </ul>

                <p className="text-slate-700 pt-2 leading-relaxed">
                  Uanset om båden skal flyttes til vinteropbevaring, værft eller ny havn, får du en løsning, hvor sikkerhed og professionalisme er i fokus.
                </p>
              </div>

              <div className="pt-6 border-t border-slate-200 mt-6">
                <Link
                  to="/baadtransport"
                  className="inline-flex items-center gap-2 text-cyan-700 hover:text-cyan-600 font-semibold text-sm"
                >
                  <span>Læs mere om Bådtransport her</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Box 2: Fleksibel bådtransport (Sejlads fra havn til havn) */}
            <div className="bg-maritime-900 text-white rounded-2xl p-8 sm:p-10 border border-maritime-800 shadow-xl flex flex-col justify-between">
              <div className="space-y-5">
                <div className="w-12 h-12 rounded-xl bg-cyan-500 text-maritime-950 flex items-center justify-center text-xl shadow-md">
                  <i className="fa-solid fa-water" aria-hidden="true" />
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                  Fleksibel bådtransport
                </h2>

                <p className="text-slate-200 leading-relaxed">
                  Hos JT Transportsejlads tilbyder jeg fleksibel bådtransport, hvor jeg sejler din båd derhen, hvor du ønsker. Det kan være:
                </p>

                <ul className="space-y-3 text-slate-200">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-cyan-400/20 text-cyan-300 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">
                      <i className="fa-solid fa-anchor" aria-hidden="true" />
                    </span>
                    <span>Fra én havn til en anden – f.eks. ved køb eller salg af båd</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-cyan-400/20 text-cyan-300 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">
                      <i className="fa-solid fa-anchor" aria-hidden="true" />
                    </span>
                    <span>Sæsonflytning mellem vinter- og sommerplads</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-cyan-400/20 text-cyan-300 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">
                      <i className="fa-solid fa-anchor" aria-hidden="true" />
                    </span>
                    <span>Transport til værft for reparation eller vedligehold</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-cyan-400/20 text-cyan-300 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">
                      <i className="fa-solid fa-anchor" aria-hidden="true" />
                    </span>
                    <span>Hjemsejlads efter ferie eller stævne</span>
                  </li>
                </ul>

                <p className="text-slate-300 pt-2 leading-relaxed">
                  Jeg planlægger ruten ud fra vejrforhold, afstand og tidsramme – og jeg holder dig løbende opdateret undervejs, så du altid ved, hvor din båd befinder sig.
                </p>
              </div>

              <div className="pt-6 border-t border-maritime-800 mt-6 flex flex-wrap items-center justify-between gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-semibold text-sm"
                >
                  <span>Kontakt mig om din rute</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>

                <a
                  href="tel:+4550510062"
                  className="text-xs text-slate-300 hover:text-white font-medium whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-cyan-400 mr-1.5" aria-hidden="true" />
                  +45 50 51 00 62
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: ERFARING OG UDDANNELSE & HVAD KOSTER BÅDTRANSPORT */}
      <section className="py-16 sm:py-20 bg-slate-100 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            {/* Image side - Maritime dæk og fortøjning */}
            <div className="lg:col-span-5 order-2 lg:order-1 flex flex-col h-full">
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white bg-slate-200 relative h-full min-h-[340px] lg:min-h-full flex flex-col">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/a8b5f246-9eaa-4cb1-adf9-4b882abefbbd/gettyimages-1126381112.jpg"
                  alt="Fortøjningsreb bundet til en klampe på båddæk"
                  className="w-full h-full object-cover flex-1"
                  loading="lazy"
                  width="800"
                  height="600"
                />
              </div>
            </div>

            {/* Text side */}
            <div className="lg:col-span-7 order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 text-cyan-700 font-semibold text-xs sm:text-sm tracking-widest uppercase">
                <i className="fa-solid fa-certificate" aria-hidden="true" />
                <span>Uddannelse & Sikkerhed</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight leading-snug">
                Erfaring og uddannelse – din garanti for sikker sejlads
              </h2>

              <div className="space-y-4 text-slate-700 text-base sm:text-lg leading-relaxed">
                <p>
                  Jeg har været passioneret sejler i mange år og valgte tidligt at gøre min passion til profession. I 2006 opnåede jeg duelighedsbeviset, som gav mig et solidt fundament inden for søfart. I 2012 videreuddannede jeg mig til skipper, og siden da har jeg sejlet et utal af ture langs de danske kyster.
                </p>
                <p>
                  Min erfaring på vandet gør mig i stand til at håndtere alle typer transportopgaver – fra mindre motorbåde til større sejlbåde. Jeg sætter en ære i at hjælpe med transport af både med høj faglighed, omhu og ansvarlighed.
                </p>
              </div>

              {/* Price sub-section from scrape */}
              <div className="mt-8 pt-6 border-t border-slate-200/80 bg-white p-6 rounded-xl shadow-sm border">
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">
                  Hvad koster bådtransport?
                </h3>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed mb-3">
                  Prisen på bådtransport afhænger af flere faktorer – blandt andet bådens størrelse, vægt, afstanden mellem afhentning og levering samt eventuelle særlige forhold.
                </p>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed font-medium">
                  Jeg tilbyder altid en konkret pris ud fra dine behov, så du ved præcis, hvad du betaler for.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: KONTAKT FORMULAR & INFORMATION - SPLIT-LAYOUT MATCHING DESIGN REFERENCE */}
      <section className="relative py-20 sm:py-24 lg:py-32 overflow-hidden bg-maritime-950 text-white" id="kontakt-sektion">
        {/* Full-bleed background image with dark overlay & fixed background on scroll */}
        <div
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{
            backgroundImage: `url('https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=80,format=auto/assets/a8b5f246-9eaa-4cb1-adf9-4b882abefbbd/gettyimages-170088390.jpg')`
          }}
          aria-hidden="true"
        >
          <div className="absolute inset-0 bg-maritime-950/90 sm:bg-maritime-950/85 backdrop-blur-[2px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left Column: Display heading, body text, attribution/link, and ghost button */}
            <div className="space-y-6">
              <span className="text-cyan-400 font-semibold text-xs sm:text-sm tracking-widest uppercase block">
                Kontakt mig i dag
              </span>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-snug">
                Skal du have flyttet din båd?
              </h2>

              <p className="text-slate-200 text-base sm:text-lg leading-relaxed">
                Uanset om båden skal sejles mellem to havne eller flyttes sikkert på trailer, står jeg klar til at hjælpe dig. Kontakt mig for en uforpligtende snak og et skræddersyet tilbud.
              </p>

              {/* Attribution / Virksomhedsoplysninger */}
              <div className="pt-6 border-t border-white/15 space-y-3 text-sm text-slate-300">
                <p className="font-semibold text-white text-base">
                  JT transportsejlads <span className="text-xs text-slate-400 font-normal">· CVR nr.: 45455335</span>
                </p>
                <div className="flex items-center gap-2.5 text-slate-300">
                  <i className="fa-solid fa-location-dot text-cyan-400 text-sm" aria-hidden="true" />
                  <a
                    href="https://www.google.com/maps/place/Moesg%C3%A5rd%20All%C3%A9%205,%208320%20M%C3%A5rslet"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-cyan-300 transition-colors"
                  >
                    Moesgård Allé 5, 8320 Mårslet
                  </a>
                  <span className="text-xs text-slate-400 font-medium">(Mårslet · Aarhus · Horsens)</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-slate-300 pt-1">
                  <a
                    href="tel:+4550510062"
                    className="inline-flex items-center gap-2 hover:text-cyan-300 transition-colors whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone text-cyan-400" aria-hidden="true" />
                    <span>+45 50 51 00 62</span>
                  </a>
                  <a
                    href="mailto:johann2@hotmail.com"
                    className="inline-flex items-center gap-2 hover:text-cyan-300 transition-colors whitespace-nowrap"
                  >
                    <i className="fa-solid fa-envelope text-cyan-400" aria-hidden="true" />
                    <span>johann2@hotmail.com</span>
                  </a>
                </div>
              </div>

              {/* Outlined primary action button (ghost button) */}
              <div className="pt-4">
                <a
                  href="tel:+4550510062"
                  className="inline-flex items-center gap-3 px-8 py-3.5 rounded-full border border-white/80 hover:border-white text-white font-semibold text-sm hover:bg-white/10 transition-all focus:outline-none focus:ring-2 focus:ring-cyan-400"
                >
                  <i className="fa-solid fa-phone text-cyan-400" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring på +45 50 51 00 62</span>
                </a>
              </div>
            </div>

            {/* Right Column: Contact form with minimal bottom-border inputs and solid pill CTA button */}
            <div className="bg-maritime-900/60 backdrop-blur-md rounded-2xl p-6 sm:p-8 lg:p-10 border border-white/10 shadow-2xl">
              <div className="mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                  Udfyld kontaktformularen her
                </h3>
                <p className="text-sm text-slate-300 mt-1">
                  Få en uforpligtende snak og et konkret tilbud på din bådtransport på vandet.
                </p>
              </div>

              {formStatus === 'success' ? (
                <div className="rounded-xl bg-emerald-950/80 border border-emerald-500/50 p-6 text-center space-y-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto text-xl">
                    <i className="fa-solid fa-check" aria-hidden="true" />
                  </div>
                  <h4 className="text-lg font-bold text-white">Tak for din henvendelse!</h4>
                  <p className="text-sm text-slate-200">
                    Jeg har modtaget din besked og vender hurtigt tilbage til dig med en vurdering af transportopgaven.
                  </p>
                  <button
                    type="button"
                    onClick={() => setFormStatus('idle')}
                    className="inline-flex items-center gap-2 mt-2 px-5 py-2.5 rounded-full bg-cyan-600 text-white text-xs font-semibold hover:bg-cyan-500 transition-colors"
                  >
                    <span>Send endnu en besked</span>
                  </button>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} noValidate className="space-y-6">
                  {/* Honeypot field (anti-spam) */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <label htmlFor="website_url">Hjemmesideadresse (lad feltet stå tomt)</label>
                    <input
                      id="website_url"
                      type="text"
                      name="website_url"
                      tabIndex="-1"
                      autoComplete="off"
                      value={formData.website_url}
                      onChange={handleFormChange}
                    />
                  </div>

                  {formStatus === 'error' && (
                    <div className="rounded-lg bg-red-950/80 border border-red-500/50 p-4 text-sm text-red-200 flex items-start gap-3">
                      <i className="fa-solid fa-circle-exclamation text-red-400 mt-0.5" aria-hidden="true" />
                      <div>
                        <p className="font-semibold text-white">Kunne ikke indsende</p>
                        <p>{formErrorMessage}</p>
                      </div>
                    </div>
                  )}

                  {/* Top row: 50/50 grid for Navn and Telefon */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="hp-navn" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                        Navn <span className="text-cyan-400">*</span>
                      </label>
                      <input
                        id="hp-navn"
                        type="text"
                        name="navn"
                        required
                        value={formData.navn}
                        onChange={handleFormChange}
                        placeholder="Dit fulde navn"
                        className={`w-full bg-transparent border-0 border-b py-2.5 text-white placeholder:text-white/40 text-sm outline-none transition-colors rounded-none focus:ring-0 ${
                          formErrors.navn ? 'border-red-400' : 'border-white/30 focus:border-cyan-400'
                        }`}
                      />
                      {formErrors.navn && <p className="text-xs text-red-400 mt-1">{formErrors.navn}</p>}
                    </div>

                    <div>
                      <label htmlFor="hp-telefon" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                        Telefonnummer <span className="text-cyan-400">*</span>
                      </label>
                      <input
                        id="hp-telefon"
                        type="tel"
                        name="telefon"
                        required
                        value={formData.telefon}
                        onChange={handleFormChange}
                        placeholder="F.eks. +45 50 51 00 62"
                        className={`w-full bg-transparent border-0 border-b py-2.5 text-white placeholder:text-white/40 text-sm outline-none transition-colors rounded-none focus:ring-0 ${
                          formErrors.telefon ? 'border-red-400' : 'border-white/30 focus:border-cyan-400'
                        }`}
                      />
                      {formErrors.telefon && <p className="text-xs text-red-400 mt-1">{formErrors.telefon}</p>}
                    </div>
                  </div>

                  {/* Email spanning full width */}
                  <div>
                    <label htmlFor="hp-email" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                      Emailadresse <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      id="hp-email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleFormChange}
                      placeholder="din-email@adresse.dk"
                      className={`w-full bg-transparent border-0 border-b py-2.5 text-white placeholder:text-white/40 text-sm outline-none transition-colors rounded-none focus:ring-0 ${
                        formErrors.email ? 'border-red-400' : 'border-white/30 focus:border-cyan-400'
                      }`}
                    />
                    {formErrors.email && <p className="text-xs text-red-400 mt-1">{formErrors.email}</p>}
                  </div>

                  {/* Båd eller havne */}
                  <div>
                    <label htmlFor="hp-baad" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                      Bådmodel / Havne (valgfri)
                    </label>
                    <input
                      id="hp-baad"
                      type="text"
                      name="baad"
                      value={formData.baad}
                      onChange={handleFormChange}
                      placeholder="F.eks. 32 fods sejlbåd fra Horsens til Aarhus"
                      className="w-full bg-transparent border-0 border-b border-white/30 focus:border-cyan-400 py-2.5 text-white placeholder:text-white/40 text-sm outline-none transition-colors rounded-none focus:ring-0"
                    />
                  </div>

                  {/* Beskrivelse spanning full width */}
                  <div>
                    <label htmlFor="hp-beskrivelse" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                      Beskrivelse af opgaven <span className="text-cyan-400">*</span>
                    </label>
                    <textarea
                      id="hp-beskrivelse"
                      name="beskrivelse"
                      rows={4}
                      required
                      value={formData.beskrivelse}
                      onChange={handleFormChange}
                      placeholder="Beskriv bådtype, strækning, eventuelle særlige ønsker..."
                      className={`w-full bg-transparent border-0 border-b py-2.5 text-white placeholder:text-white/40 text-sm outline-none transition-colors rounded-none resize-y focus:ring-0 ${
                        formErrors.beskrivelse ? 'border-red-400' : 'border-white/30 focus:border-cyan-400'
                      }`}
                    />
                    {formErrors.beskrivelse && <p className="text-xs text-red-400 mt-1">{formErrors.beskrivelse}</p>}
                  </div>

                  {/* Solid pill-shaped primary CTA button spanning full width */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-4 rounded-full bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-sm sm:text-base shadow-xl hover:shadow-cyan-500/25 transition-all flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-cyan-400 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {formStatus === 'submitting' ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                          <span>Sender din henvendelse...</span>
                        </>
                      ) : (
                        <>
                          <span>Send henvendelse</span>
                          <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
